import { sanitizeAeroPressGrind } from "@client-shared/utilities/productSyncUtilities";
import { calculateWeightFromBagSize } from "@client-shared/utilities/weightUtilities";
import type { CreateConnectionParams, FormProduct, FormVariant, GrindType, Product, ProductConnection, Variant } from "@shared/types";
import type React from "react";
import type { PreConnection, ProductSyncOption, SelectValue, VariantPreConnections } from "./product-sync-modal.types";

export { sanitizeAeroPressGrind, setSingleParentProducts } from "@client-shared/utilities/productSyncUtilities";

export const setFilteredConnections = (connections: ProductConnection[] | undefined) => {
  return connections
    ?.filter((variant) => variant.state === "ACTIVE")
    .sort((a, b) => {
      return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
    });
};

export const setConnectedVariantIds = (filteredVariantConnections: ProductConnection[] | undefined) => {
  return filteredVariantConnections?.map((connection) => connection.targetVariantId);
};

export const setConnectedSourceObjectIds = (filteredVariantConnections: ProductConnection[] | undefined) => {
  return [...new Set(filteredVariantConnections?.map((connection) => connection.sourceObjectId))];
};

export const setFilteredRemoteConnections = (remoteProducts: Product[] | undefined, connectedRemoteVariantIds: string[] | undefined) => {
  return (
    remoteProducts?.map<Product>((product) => ({
      ...product,
      variants: product.variants?.filter((variant) => connectedRemoteVariantIds?.indexOf(variant.id) === -1),
    })) ?? []
  );
};

export const setHasNoConnectionSourceObjectIds = (product: Product, connectedSourceObjectIds: string[]) => {
  const variants = product.variants ?? [];
  if (variants.length === 0) return false;
  const connectedIds = new Set(connectedSourceObjectIds);
  return !variants.some((variant) => connectedIds.has(variant.id));
};

export const setUnmappedProducts = (localProducts: Product[] | undefined, connectedSourceObjectIds: string[]) => {
  if (!localProducts || !connectedSourceObjectIds) {
    return [];
  }
  return (
    localProducts?.filter((product) => {
      return setHasNoConnectionSourceObjectIds(product, connectedSourceObjectIds);
    }) ?? []
  );
};

// Counts distinct mapped connections by sourceObjectId. connectionParams/updatedConnections are
// session-pending edits that can overlap with filteredVariantConnections (already-active
// connections) - summing the three arrays' lengths double/triple counts the same mapping.
export const setMappedConnectionsCount = (
  requiredSourceObjectIds: string[],
  connectionParams: CreateConnectionParams[],
  updatedConnections: ProductConnection[],
  filteredVariantConnections: ProductConnection[]
) => {
  const mappedSourceObjectIds = new Set([
    ...connectionParams.map((connectionParam) => connectionParam.sourceObjectId),
    ...updatedConnections.map((connection) => connection.sourceObjectId),
    ...filteredVariantConnections.map((connection) => connection.sourceObjectId),
  ]);
  return new Set(requiredSourceObjectIds.filter((sourceObjectId) => mappedSourceObjectIds.has(sourceObjectId))).size;
};

export const deselectValues2 = (
  setConnectionParams: (value: React.SetStateAction<CreateConnectionParams[]>) => void,
  setUpdatedConnections: (value: React.SetStateAction<ProductConnection[]>) => void,
  deselectedValues?: string[],
  sourceObjectIds?: string[]
) => {
  if (!deselectedValues || deselectedValues.length === 0) {
    return;
  }

  const keepConnection = (connection: CreateConnectionParams) =>
    !deselectedValues.includes(connection.targetVariantId) ||
    (sourceObjectIds !== undefined && !sourceObjectIds.includes(connection.sourceObjectId));
  setConnectionParams((current) => current.filter(keepConnection));
  setUpdatedConnections((current) => current.filter(keepConnection));
};

export const setLabelTitle = (product: Product, variant: Variant, variantIsImportedString: string, isImportedVariant: boolean) => {
  if (isImportedVariant) {
    return `${product.title ?? ""} - ${variant.title ?? ""} ${variantIsImportedString}`;
  }
  return `${product.title ?? ""} - ${variant.title ?? ""}`;
};

export const setFlatOptions = (
  filteredRemoteProducts: Product[],
  importedVariantIds: string[],
  variantIsImportedString: string
): ProductSyncOption[] => {
  const uniqueByValue = new Map<string, ProductSyncOption>();

  filteredRemoteProducts.forEach((product) => {
    product.variants?.forEach((variant) => {
      const isImportedVariant = importedVariantIds.includes(variant.id);
      uniqueByValue.set(variant.id, {
        label: setLabelTitle(product, variant, variantIsImportedString, isImportedVariant),
        value: variant.id,
        isDisabled: isImportedVariant,
        isImportedVariant,
      });
    });
  });

  return Array.from(uniqueByValue.values());
};

const setOptionReservationState = (option: ProductSyncOption, isReserved: boolean): ProductSyncOption => {
  return {
    ...option,
    isDisabled: Boolean(option.isImportedVariant || isReserved),
  };
};

export const setOptions = (
  setAvailableOptions: (value: React.SetStateAction<ProductSyncOption[]>) => void,
  values: SelectValue[] | SelectValue,
  previousSelectedOptions?: ProductSyncOption[]
) => {
  if (!(values && Array.isArray(values) && values.length > 0)) {
    return;
  }
  const previousValues = previousSelectedOptions?.map((o) => o.value) ?? [];
  setAvailableOptions((current) => {
    return current.map((option) => {
      if (previousValues.includes(option.value)) {
        return setOptionReservationState(option, false);
      }
      if (values.includes(option.value)) {
        return setOptionReservationState(option, true);
      }
      return option;
    });
  });
};

export const deselectOptions = (
  setAvailableOptions: (value: React.SetStateAction<ProductSyncOption[]>) => void,
  deselectedValues: SelectValue[] | SelectValue
) => {
  return setAvailableOptions((current) => {
    return current.map((option) => {
      if (deselectedValues?.includes(option.value)) {
        return setOptionReservationState(option, false);
      }
      return option;
    });
  });
};

export const optionSelect =
  (setAvailableOptions: (value: React.SetStateAction<ProductSyncOption[]>) => void) =>
  (value: SelectValue[] | SelectValue, deselectedValues: SelectValue[] | SelectValue, previousSelectedOptions?: ProductSyncOption[]) => {
    if (deselectedValues && deselectedValues.length > 0) {
      return deselectOptions(setAvailableOptions, deselectedValues);
    }
    setOptions(setAvailableOptions, value, previousSelectedOptions);
  };

export const sanitiseAeroPressForConnection = (grindType: GrindType) => {
  return sanitizeAeroPressGrind(grindType as string) as GrindType;
};

export const setPreconnectionToCreateConnectionParams =
  (vendorKey: string, installationId: string) => (preConnection: PreConnection, grindType: GrindType) => {
    return {
      type: "productConnection",
      vendorKey: vendorKey,
      identifier: preConnection.identifier,
      state: "ACTIVE",
      sourceServiceId: "pim",
      sourceObjectId: preConnection.sourceObjectId,
      bagSize: preConnection.bagSize,
      grindType: sanitiseAeroPressForConnection(grindType),
      targetProductId: preConnection.targetProductId,
      targetInstallationId: installationId,
      targetVariantId: preConnection.targetVariantId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      productWeightInGrams: preConnection.productWeightInGrams,
    } satisfies CreateConnectionParams;
  };

export const setAllCreateConnectionParams = (
  sourceObjectIds: string[],
  variantPreConnections: VariantPreConnections,
  localProducts: Product[],
  selectedValues: SelectValue[],
  handleSetPreConnectionToConnection: (preConnection: PreConnection, grindType: GrindType) => CreateConnectionParams
): CreateConnectionParams[] => {
  const connectionParams = sourceObjectIds.flatMap((localProductId) => {
    const hasLocalProduct = localProducts.some((product) => {
      return product.variants?.some((variant) => variant.id === localProductId);
    });
    if (!hasLocalProduct) {
      return [];
    }
    const preConnections = variantPreConnections[localProductId];
    if (!preConnections || preConnections.length === 0) {
      return [];
    }
    return preConnections.flatMap((preConnection) => {
      const { targetVariantId, grindType } = preConnection;
      if (!grindType?.length) {
        selectedValues.push(targetVariantId);
        return [];
      }
      selectedValues.push(targetVariantId);
      return grindType?.map((grind) => handleSetPreConnectionToConnection(preConnection, grind as GrindType)) ?? [];
    });
  });
  return connectionParams.filter((param) => param.grindType.length > 0);
};

export const handleSelectValue =
  (
    localProducts: Product[],
    handleSetPreConnectionToConnection: (preConnection: PreConnection, grindType: GrindType) => CreateConnectionParams
  ) =>
  (variantPreConnections: VariantPreConnections) => {
    const sourceObjectIds = Object.keys(variantPreConnections);

    if (sourceObjectIds.length === 0) {
      return;
    }
    const selectedValues: SelectValue[] = [];
    const deselectedValues = Object.values(variantPreConnections)
      .flat()
      .filter((connection) => connection.grindType.length === 0)
      .map((connection) => connection.targetVariantId);

    const allCreateConnectionParams = setAllCreateConnectionParams(
      sourceObjectIds,
      variantPreConnections,
      localProducts,
      selectedValues,
      handleSetPreConnectionToConnection
    );

    return {
      deselectedValues,
      selectedValues,
      allCreateConnectionParams,
      sourceObjectIds,
    };
  };

export const selectSyncOptions =
  (
    handleSync: (connectionParams: CreateConnectionParams[], deselectedValues: string[], sourceObjectIds?: string[]) => void,
    findPreviousSelectedOptions: (remoteVariantIds: string[]) => ProductSyncOption[] | undefined,
    handleOptions: (
      targetVariantId: SelectValue[] | SelectValue,
      deselectedTargetVariantId: SelectValue[] | SelectValue,
      previousSelectedOptions?: ProductSyncOption[]
    ) => void
  ) =>
  (connectionParams: CreateConnectionParams[], deselectedValues: string[], selectedValues: SelectValue[], sourceObjectIds?: string[]) => {
    const values = [
      ...connectionParams.map((connection) => connection.targetVariantId),
      ...(selectedValues.length > 0 ? selectedValues : []),
    ];

    const deselectedOptions = deselectedValues.filter((value) => !selectedValues.includes(value));
    const previousSelectedOptions = findPreviousSelectedOptions(deselectedOptions);

    const effectiveDeselectedValues = () => {
      if (deselectedValues.length === 0 && connectionParams.length === 0) {
        return selectedValues as string[];
      }
      return deselectedValues ?? [];
    };

    handleSync(connectionParams, effectiveDeselectedValues(), sourceObjectIds);
    handleOptions(values, deselectedOptions, previousSelectedOptions);
  };

export const findPreviousSelectedOptions =
  (connectionParams: CreateConnectionParams[], updatedConnections: ProductConnection[], availableOptions: ProductSyncOption[]) =>
  (remoteVariantIds: string[]) => {
    const deselectedConnections =
      connectionParams.filter((connection) => remoteVariantIds.includes(connection.targetVariantId)) ||
      updatedConnections.filter((connection) => remoteVariantIds.includes(connection.targetVariantId));
    if (deselectedConnections) {
      const variants = new Set(deselectedConnections.map((connection) => connection.targetVariantId));
      return availableOptions.filter((option) => variants.has(option.value));
    }
    return undefined;
  };

export const setSyncConnections =
  (
    previousConnectionParams: CreateConnectionParams[],
    setConnectionParams: (value: React.SetStateAction<CreateConnectionParams[]>) => void,
    setUpdatedConnections: (value: React.SetStateAction<ProductConnection[]>) => void,
    connections: ProductConnection[] | undefined
  ) =>
  (connectionParams: CreateConnectionParams[], deselectedValues: string[], sourceObjectIds?: string[]) => {
    const commonTargetVariantIds = [...new Set(previousConnectionParams.map((connectionParam) => connectionParam.targetVariantId))];

    commonTargetVariantIds.forEach((targetVariantId) => {
      const newCount = connectionParams.filter((connection) => connection.targetVariantId === targetVariantId).length;
      const oldCount = previousConnectionParams.filter((connection) => connection.targetVariantId === targetVariantId).length;

      if (oldCount >= newCount && newCount > 0) {
        setConnectionsWithValues({
          setConnectionParams,
          setUpdatedConnections,
          connections,
          connectionParams,
          deselectedValues: [targetVariantId],
          sourceObjectIds,
        });
      }
    });

    setConnectionsWithValues({
      setConnectionParams,
      setUpdatedConnections,
      connections,
      connectionParams,
      deselectedValues,
      sourceObjectIds,
    });
  };

export const setConnectionsWithValues = ({
  setConnectionParams,
  setUpdatedConnections,
  connections,
  connectionParams,
  deselectedValues,
  sourceObjectIds,
}: {
  setConnectionParams: (value: React.SetStateAction<CreateConnectionParams[]>) => void;
  setUpdatedConnections: (value: React.SetStateAction<ProductConnection[]>) => void;
  connections: ProductConnection[] | undefined;
  connectionParams: CreateConnectionParams[];
  deselectedValues?: string[];
  sourceObjectIds?: string[];
}) => {
  if (deselectedValues && deselectedValues.length > 0) {
    deselectValues2(setConnectionParams, setUpdatedConnections, deselectedValues, sourceObjectIds);
  }
  const remainingConnectionParams = connectionParams.filter(
    (connectionParam) => !deselectedValues?.includes(connectionParam.targetVariantId)
  );
  if (remainingConnectionParams.length === 0) {
    return;
  }

  remainingConnectionParams.forEach((connectionParam) => {
    const matches = connections?.filter(
      (existingConnection) =>
        existingConnection.sourceObjectId.toUpperCase() === connectionParam.sourceObjectId.toUpperCase() &&
        existingConnection.grindType === connectionParam.grindType &&
        existingConnection.targetInstallationId === connectionParam.targetInstallationId
    );
    const connection = matches?.find((entry) => entry.state === "ACTIVE") ?? matches?.find((entry) => entry.state === "PAUSED");

    updateConnectionsWithValue(connection, setUpdatedConnections, connectionParam);
    createConnectionParamsWithValue({
      isConnection: Boolean(connection),
      setConnectionParams,
      connectionParam,
    });
  });
};

export const createConnectionParamsWithValue = ({
  isConnection,
  setConnectionParams,
  connectionParam,
}: {
  isConnection: boolean;
  setConnectionParams: (value: React.SetStateAction<CreateConnectionParams[]>) => void;
  connectionParam: CreateConnectionParams;
}) => {
  if (isConnection) {
    return;
  }
  setConnectionParams((current) => {
    const otherConnections = current.filter(
      (connection) =>
        connection.sourceObjectId.toUpperCase() !== connectionParam.sourceObjectId.toUpperCase() ||
        connection.grindType !== connectionParam.grindType ||
        connection.targetInstallationId !== connectionParam.targetInstallationId
    );
    return [...otherConnections, connectionParam];
  });
};

export const updateConnectionsWithValue = (
  connection: ProductConnection | undefined,
  setUpdatedConnections: (value: React.SetStateAction<ProductConnection[]>) => void,
  connectionParam: CreateConnectionParams
) => {
  if (!connection) {
    return;
  }

  const updatedConnection: ProductConnection = {
    ...connection,
    ...connectionParam,
    createdAt: connection.createdAt,
  };
  setUpdatedConnections((current) => {
    if (updatedConnection.grindType.length === 0) {
      return current;
    }
    return [
      ...current.filter(
        (entry) =>
          entry.sourceObjectId.toUpperCase() !== updatedConnection.sourceObjectId.toUpperCase() || entry.id !== updatedConnection.id
      ),
      updatedConnection,
    ];
  });
};

export const setInitialPreConnections = (variants: Variant[] | undefined) => {
  if (!variants || variants.length === 0) {
    return undefined;
  }
  return variants.reduce<VariantPreConnections>((acc, variant) => {
    return {
      ...acc,
      [variant.id]: [],
    };
  }, {});
};

export const setPreConnections = (
  sourceObjectId: string,
  targetVariantId: string,
  grindType: string[],
  localVariants: Variant[],
  getRemoteFormProduct: (targetVariantId: string) => FormProduct | undefined
) => {
  const localVariant = localVariants?.find((variant) => variant.id === sourceObjectId);
  const localVariantWeightInGrams = calculateWeightFromBagSize(localVariant?.bagSize as string) ?? 0;

  if (!targetVariantId) {
    return;
  }
  const remoteFormProduct = getRemoteFormProduct(targetVariantId);
  if (!remoteFormProduct) {
    return;
  }
  const remoteVariant = remoteFormProduct.variants?.find((variant) => variant.id === targetVariantId);
  if (!remoteVariant) {
    return;
  }
  return {
    targetVariantId: targetVariantId,
    grindType: grindType ?? [],
    targetProductId: remoteFormProduct.id,
    bagSize: localVariant?.bagSize as string,
    identifier: `${remoteFormProduct.title ?? ""} - ${remoteVariant.title ?? ""}`,
    sourceObjectId: sourceObjectId,
    productWeightInGrams: localVariantWeightInGrams,
  } satisfies PreConnection;
};

export const getOptionFromValues = (values: string | string[] | ProductSyncOption[], options: ProductSyncOption[]): ProductSyncOption[] => {
  if (!values) {
    return [];
  }

  if (typeof values === "string") {
    const option = options.find((entry) => entry.value === values);
    return option ? [option] : [];
  }

  if (!Array.isArray(values)) {
    return [];
  }

  if (values.length > 0 && typeof values[0] === "object" && values[0] !== null && "value" in values[0]) {
    return values as ProductSyncOption[];
  }

  const selectedOptions = (values as string[]).map((value) => {
    return options.find((option) => option.value === value);
  });
  return selectedOptions.filter((option): option is ProductSyncOption => option !== undefined);
};

export const getRemoteFormVariantData = (
  getRemoteFormProduct: (targetVariantId: string) => FormProduct | undefined,
  targetVariantId: string
): { remoteFormVariant?: FormVariant; remoteProductTitle?: string } => {
  const remoteFormProduct = getRemoteFormProduct(targetVariantId);
  if (!remoteFormProduct) {
    return {};
  }
  return {
    remoteFormVariant: remoteFormProduct.variants?.find((variant) => variant.id === targetVariantId),
    remoteProductTitle: remoteFormProduct.title,
  };
};
